import {
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';
import axios from 'axios';

// Auth0 types removed - using inline function signature for portability
import { type QueryRouteBarrelTypes, QueryRouteBarrelVals } from '@core/api';
import type { List } from '@core/types/list';

import type { QueryError } from '../utils/Query';
import { SlabConfig } from '../utils/SlabConfig';
import { useSlabAuth } from './useSlabAuth';
import { UrlFromParams } from './useSlabQuery';

type Params = Record<string, object | string | number | boolean>;

// Only allow routes that return List<T> (paginated responses)
type ListRouteKey = {
  [K in keyof QueryRouteBarrelTypes]: QueryRouteBarrelTypes[K]['returns'] extends List<any>
    ? K
    : never;
}[keyof QueryRouteBarrelTypes];

// Helper type to omit pagination params from queryParams
type OmitPaginationParams<T> = T extends { queryParams?: infer Q }
  ? Omit<T, 'queryParams'> & {
      queryParams?: Omit<Q, 'page' | 'perPage'>;
    }
  : T;

export type UseSlabInfiniteQueryType<
  TKey extends keyof QueryRouteBarrelTypes,
  TBarrel extends QueryRouteBarrelTypes[TKey] = QueryRouteBarrelTypes[TKey],
  TError extends QueryError = QueryError,
> = {
  routeBarrelKey: TKey & ListRouteKey;
  args: OmitPaginationParams<TBarrel['args']>;
  pageSize?: number;
  options?:
    | Omit<
        UseInfiniteQueryOptions<
          TBarrel['returns'],
          TError,
          InfiniteData<TBarrel['returns']>,
          QueryKey,
          number
        >,
        'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
      >
    | undefined;
};

export const MakeInfiniteQueryArgs = <
  TKey extends keyof QueryRouteBarrelTypes,
  TBarrel extends QueryRouteBarrelTypes[TKey] = QueryRouteBarrelTypes[TKey],
  TError extends QueryError = QueryError,
>(
  getAccessTokenSilently: (options: {
    authorizationParams: { audience: string };
  }) => Promise<string>,
  routeBarrelKey: UseSlabInfiniteQueryType<TKey, TBarrel, TError>['routeBarrelKey'],
  args: OmitPaginationParams<TBarrel['args']>,
  pageSize: number,
  options?: UseSlabInfiniteQueryType<TKey, TBarrel, TError>['options'],
): UseInfiniteQueryOptions<
  TBarrel['returns'],
  TError,
  InfiniteData<TBarrel['returns']>,
  QueryKey,
  number
> => {
  const barrel = QueryRouteBarrelVals[routeBarrelKey];

  const getObject = async (page: number, signal?: AbortSignal): Promise<TBarrel['returns']> => {
    // Check the user has a valid access token
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: SlabConfig.auth0.audience,
      },
    });

    // Build URL with pagination params
    const queryParams = {
      ...(((args as any)?.queryParams as Params) ?? {}),
      page,
      perPage: pageSize,
    };

    const fullUrl = UrlFromParams({
      queryParams,
      pathParams: ((args as any)?.pathParams as Params) ?? {},
      urlSplits: barrel.urlSplits,
    });

    // Extract the raw ID_Token into the authorization bearer header
    const headers = {
      headers: {
        ...(barrel.headers ?? {}),
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const { data } = await axios.get(fullUrl, { ...headers, signal });
    return barrel.new(data);
  };

  // Build query key without pagination params (so it's stable across pages)
  const queryKeyUrl = UrlFromParams({
    queryParams: ((args as any)?.queryParams as Params) ?? {},
    pathParams: ((args as any)?.pathParams as Params) ?? {},
    urlSplits: barrel.urlSplits,
  });

  return {
    ...options,
    queryKey: [queryKeyUrl, routeBarrelKey, 'infinite'],
    queryFn: ({ pageParam, signal }) => getObject(pageParam, signal),
    getNextPageParam: (lastPage, allPages): number | undefined => {
      // TKey is constrained to ListRouteKey, so lastPage is always List<T>
      const page = lastPage as List<any>;
      const loadedCount = allPages.reduce((sum, p) => sum + (p as List<any>).items.length, 0);
      const isLastPageFull = page.items.length === pageSize;
      const hasMoreItems = loadedCount < page.count;
      return isLastPageFull && hasMoreItems ? allPages.length : undefined;
    },
    initialPageParam: 0,
  };
};

export const useSlabInfiniteQuery = <
  TKey extends keyof QueryRouteBarrelTypes,
  TBarrel extends QueryRouteBarrelTypes[TKey] = QueryRouteBarrelTypes[TKey],
  TError extends QueryError = QueryError,
>(
  routeBarrelKey: UseSlabInfiniteQueryType<TKey, TBarrel, TError>['routeBarrelKey'],
  args: UseSlabInfiniteQueryType<TKey, TBarrel, TError>['args'],
  pageSize: number = 25,
  options?: UseSlabInfiniteQueryType<TKey, TBarrel, TError>['options'],
): UseInfiniteQueryResult<InfiniteData<TBarrel['returns']>, TError> => {
  const { getAccessTokenSilently } = useSlabAuth();
  return useInfiniteQuery<
    TBarrel['returns'],
    TError,
    InfiniteData<TBarrel['returns']>,
    QueryKey,
    number
  >(MakeInfiniteQueryArgs(getAccessTokenSilently, routeBarrelKey, args, pageSize, options));
};
